/* ═══════════════════════════════════════════════════════════════
   orgChart — pure client mirror of ec2-api/server.js's
   isBelowOnOrgChart (server.js:3240-3254), so the UI never offers an
   assignee the server will refuse. Same semantics: walks
   team_members.reports_to upward from the TARGET member, true when
   `managerId` appears anywhere in that chain, false for self, depth
   capped so a reports_to cycle can't spin forever. Do NOT change the
   server rule here — this is a read-only mirror (Decision 11).
   ═══════════════════════════════════════════════════════════════ */

/** The only shape this module needs from a team member row. */
export interface OrgChartMember {
  id: string;
  reportsTo?: string | null;
}

/** Matches the server's recursive CTE depth cap (`c.depth < 50`). */
const MAX_DEPTH = 50;

/** True when `memberId` sits somewhere below `managerId` on the org chart —
 *  i.e. `managerId` is `memberId`'s direct manager, or their manager's
 *  manager, etc. `memberId === managerId` is always false (a caller cannot
 *  assign themselves via `assign_team`). */
export function isBelowOnOrgChart(
  members: OrgChartMember[],
  memberId: string,
  managerId: string
): boolean {
  if (!memberId || !managerId || memberId === managerId) return false;
  const byId = new Map<string, OrgChartMember>(members.map((m) => [m.id, m]));
  let currentId: string | null | undefined = memberId;
  for (let depth = 0; depth < MAX_DEPTH; depth++) {
    const node: OrgChartMember | undefined = currentId ? byId.get(currentId) : undefined;
    const reportsTo: string | null | undefined = node?.reportsTo;
    if (!reportsTo) return false;
    if (reportsTo === managerId) return true;
    currentId = reportsTo;
  }
  return false;
}

/** The subset of `members` `callerMemberId` may assign, mirroring the
 *  server's enforcement (server.js:3694-3713): `assignAnyone` bypasses
 *  entirely; else `assignTeam` is required AND every assignee must pass
 *  `isBelowOnOrgChart`; neither permission → nobody. */
export function assignableMembers<T extends OrgChartMember>(
  members: T[],
  callerMemberId: string,
  perms: { assignAnyone: boolean; assignTeam: boolean }
): T[] {
  if (perms.assignAnyone) return members;
  if (!perms.assignTeam || !callerMemberId) return [];
  return members.filter((m) => isBelowOnOrgChart(members, m.id, callerMemberId));
}
